import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Package, Calendar, Clock, User, Award, Star, Shield,
  Crown, Sparkles, Save, Heart, Truck, Gift, CheckCircle,
  XCircle, MapPin, Box, Send, MessageSquare, TicketCheck, Dog, Cat
} from 'lucide-react';

import { API, authOpts, getAuthHeaders } from '../config/api';

const BADGE_ICONS = { 1: Star, 2: Sparkles, 3: Award, 4: Crown };

const MONTHS_RO = ['Ianuarie','Februarie','Martie','Aprilie','Mai','Iunie','Iulie','August','Septembrie','Octombrie','Noiembrie','Decembrie'];
const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function ProfileTab({ profile, setProfile, t, isRomanian }) {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/profile`, {
        method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          pet_name: profile.pet_name || null,
          pet_type: profile.pet_type || 'dog',
          pet_age: profile.pet_age || null,
          dog_size: profile.dog_size || null,
          address: profile.address || null,
          city: profile.city || null,
          county: profile.county || null,
          zip_code: profile.zip_code || null,
          phone: profile.phone || null,
          contact_email: profile.contact_email || null,
        }),
      });
      if (res.ok) { setProfile(await res.json()); toast.success(t('dash_saved')); }
    } catch { toast.error('Error'); } finally { setSaving(false); }
  };

  const update = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));

  const showDogSize = profile.pet_type === 'dog';

  return (
    <div className="space-y-6">
      <div className="bg-brand-orange/5 border border-brand-orange/20 rounded-2xl p-5 flex items-start gap-3">
        <Heart className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
        <p className="text-sm text-brand-muted leading-relaxed">{t('dash_profile_why')}</p>
      </div>

      <div className="bg-white rounded-2xl border border-brand-border/30 p-6 space-y-4">
        <h3 className="font-heading font-semibold text-brand-brown flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand-orange" /> {t('dash_profile_title')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1.5">{t('dash_pet_name')}</label>
            <Input value={profile.pet_name || ''} onChange={(e) => update('pet_name', e.target.value)} className="input-field" data-testid="profile-pet-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1.5">{t('dash_pet_type')}</label>
            <select value={profile.pet_type || 'dog'} onChange={(e) => update('pet_type', e.target.value)} className="input-field w-full" data-testid="profile-pet-type">
              <option value="dog">{t('pet_dogs')}</option>
              <option value="cat">{t('pet_cats')}</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1.5">{t('dash_pet_age')}</label>
            <Input value={profile.pet_age || ''} onChange={(e) => update('pet_age', e.target.value)} placeholder="2 years" className="input-field" data-testid="profile-pet-age" />
          </div>
          {showDogSize && (
            <div>
              <label className="block text-sm font-medium text-brand-brown mb-1.5">{t('dash_dog_size')}</label>
              <select value={profile.dog_size || ''} onChange={(e) => update('dog_size', e.target.value)} className="input-field w-full" data-testid="profile-dog-size">
                <option value="">-</option>
                <option value="small">{t('funnel_size_small')}</option>
                <option value="medium">{t('funnel_size_medium')}</option>
                <option value="large">{t('funnel_size_large')}</option>
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-border/30 p-6 space-y-4">
        <h3 className="font-heading font-semibold text-brand-brown flex items-center gap-2">
          <MapPin className="w-4 h-4 text-brand-orange" /> {t('dash_address')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1.5">{t('dash_city')}</label>
            <Input value={profile.city || ''} onChange={(e) => update('city', e.target.value)} placeholder={isRomanian ? 'Oraș / Localitate' : 'City'} className="input-field" data-testid="profile-city" />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1.5">{isRomanian ? 'Județ' : 'County'}</label>
            <Input value={profile.county || ''} onChange={(e) => update('county', e.target.value)} className="input-field" data-testid="profile-county" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-brown mb-1.5">{t('funnel_address')}</label>
          <Input value={profile.address || ''} onChange={(e) => update('address', e.target.value)} placeholder={isRomanian ? 'Strada, nr, bloc, ap...' : 'Street, number, building...'} className="input-field" data-testid="profile-address" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1.5">{isRomanian ? 'Cod poștal' : 'Zip code'}</label>
            <Input value={profile.zip_code || ''} onChange={(e) => update('zip_code', e.target.value)} className="input-field" data-testid="profile-zipcode" />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1.5">{t('dash_phone')}</label>
            <Input value={profile.phone || ''} onChange={(e) => update('phone', e.target.value)} placeholder="+40..." className="input-field" data-testid="profile-phone" />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1.5">{t('dash_contact_email')}</label>
            <Input value={profile.contact_email || ''} onChange={(e) => update('contact_email', e.target.value)} className="input-field" data-testid="profile-contact-email" />
          </div>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} className="btn-primary w-full md:w-auto flex items-center justify-center gap-2" data-testid="profile-save-btn">
        <Save className="w-4 h-4" /> {saving ? '...' : t('dash_save')}
      </button>
    </div>
  );
}

function SubscriptionTab({ subscriptions, boxes, language, t }) {
  const navigate = useNavigate();
  const isRomanian = language === 'ro';
  const MONTHS = isRomanian ? MONTHS_RO : MONTHS_EN;
  const hasActive = subscriptions.some(s => s.status === 'active');

  const handleCancel = async (subId) => {
    if (!window.confirm(t('dash_cancel_confirm'))) return;
    try {
      const res = await fetch(`${API}/subscriptions/${subId}/cancel`, {
        method: 'POST', credentials: 'include', headers: getAuthHeaders()
      });
      if (res.ok) { toast.success(t('dash_cancelled')); window.location.reload(); }
    } catch { toast.error('Error'); }
  };

  const handleSkip = async (subId) => {
    if (!window.confirm(t('dash_skip_confirm'))) return;
    try {
      const res = await fetch(`${API}/subscriptions/${subId}/skip`, {
        method: 'POST', credentials: 'include', headers: getAuthHeaders()
      });
      if (res.ok) { toast.success(t('dash_skipped')); window.location.reload(); }
      else {
        const data = await res.json();
        toast.error(data.detail || 'Error');
      }
    } catch { toast.error('Error'); }
  };

  // Filter boxes: only show delivered/shipped, not future "preparing" ones
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const visibleBoxes = boxes.filter(box => {
    if (box.status === 'delivered' || box.status === 'shipped') return true;
    // Only show current month's preparing box, not future months
    if (box.box_year < currentYear) return true;
    if (box.box_year === currentYear && box.box_month <= currentMonth) return true;
    return false;
  });

  if (!subscriptions.length) {
    return (
      <div className="text-center py-16">
        <div className="w-24 h-24 bg-brand-orange/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Package className="w-12 h-12 text-brand-orange" />
        </div>
        <h3 className="font-heading text-2xl font-semibold text-brand-brown mb-2">{t('dash_no_sub')}</h3>
        <p className="text-brand-muted mb-6">{t('dash_no_sub_desc')}</p>
        <button onClick={() => navigate('/')} className="btn-primary" data-testid="go-to-plans">{t('dash_see_plans')}</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {subscriptions.map(sub => (
        <div key={sub.subscription_id} className="bg-white rounded-2xl border border-brand-border/30 p-6" data-testid={`subscription-${sub.subscription_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              {sub.pet_type === 'cat' ? <Cat className="w-8 h-8 text-brand-brown" /> : <Dog className="w-8 h-8 text-brand-orange" />}
              <div>
                <h3 className="font-heading text-lg font-semibold text-brand-brown">
                  {sub.pet_type === 'cat' ? t('pricing_cat_box') : t('pricing_dog_box')}
                </h3>
                <p className="text-sm text-brand-muted">{sub.plan_type.replace('_', ' ')}</p>
              </div>
            </div>
            <Badge className={sub.status === 'active' ? 'bg-brand-success text-white' : 'bg-brand-muted text-white'}>
              {t(`status_${sub.status}`) || sub.status}
            </Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-brand-muted">{t('pricing_1_month') && 'Price'}:</span> <span className="font-semibold">{sub.price} {sub.currency === 'RON' ? 'lei' : '€'}</span></div>
            <div><span className="text-brand-muted">Start:</span> <span className="font-semibold">{new Date(sub.start_date).toLocaleDateString()}</span></div>
            {sub.next_billing_date && <div><span className="text-brand-muted">{t('dash_next_billing')}:</span> <span className="font-semibold">{new Date(sub.next_billing_date).toLocaleDateString()}</span></div>}
          </div>
          
          {/* Next box info */}
          {sub.status === 'active' && sub.start_date && (() => {
            const start = new Date(sub.start_date);
            const day = start.getDate();
            const now = new Date();
            const nextMonth = now.getDate() > day 
              ? new Date(now.getFullYear(), now.getMonth() + 1, day) 
              : new Date(now.getFullYear(), now.getMonth(), day);
            if (nextMonth < now) nextMonth.setMonth(nextMonth.getMonth() + 1);
            return (
              <div className="mt-3 flex items-center gap-2 bg-brand-orange/10 px-4 py-2.5 rounded-xl">
                <Calendar className="w-4 h-4 text-brand-orange" />
                <p className="text-sm text-brand-brown">
                  <span className="font-semibold">
                    {isRomanian ? 'Următoarea cutie' : 'Next box'}:
                  </span> {isRomanian ? `va fi trimisă pe ${day} ${MONTHS_RO[nextMonth.getMonth()]}` : `will be shipped on ${MONTHS_EN[nextMonth.getMonth()]} ${day}`}
                </p>
              </div>
            );
          })()}
          
          {/* Tracking code if available */}
          {sub.tracking_code && (
            <div className="mt-3 flex items-center gap-2 bg-indigo-50 px-4 py-2.5 rounded-xl">
              <Truck className="w-4 h-4 text-indigo-600" />
              <div className="text-sm">
                <span className="text-gray-600">{isRomanian ? 'Expediat cu DPD Romania' : 'Shipped by DPD Romania'}:</span>{' '}
                <a href={`https://www.speedy.bg/public/index.php/en/track-shipment?shipmentNumber=${sub.tracking_code}`}
                  target="_blank" rel="noopener noreferrer"
                  className="font-mono font-bold text-indigo-600 hover:underline">
                  {sub.tracking_code}
                </a>
              </div>
            </div>
          )}
          {sub.skipped_months && sub.skipped_months.length > 0 && (
            <p className="text-sm text-brand-orange mt-2">{t('dash_skipped_months')}: {sub.skipped_months.join(', ')}</p>
          )}
          {sub.status === 'active' && (
            <div className="mt-4 flex items-center gap-4">
              <button onClick={() => handleSkip(sub.subscription_id)} className="text-sm text-brand-orange hover:underline flex items-center gap-1" data-testid={`skip-sub-${sub.subscription_id}`}>
                <Calendar className="w-3.5 h-3.5" /> {t('dash_skip_month')}
              </button>
              <button onClick={() => handleCancel(sub.subscription_id)} className="text-sm text-red-500 hover:underline flex items-center gap-1" data-testid={`cancel-sub-${sub.subscription_id}`}>
                <XCircle className="w-3.5 h-3.5" /> {t('dash_cancel_sub')}
              </button>
            </div>
          )}
        </div>
      ))}

      {visibleBoxes.length > 0 && (
        <div className="mt-8">
          <h3 className="font-heading text-xl font-semibold text-brand-brown mb-4">{t('dash_boxes_tab')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleBoxes.map((box, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-brand-border/30 p-4 flex items-center gap-4" data-testid={`box-${idx}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${box.status === 'delivered' ? 'bg-brand-success/20' : box.status === 'shipped' ? 'bg-brand-orange/20' : 'bg-brand-cream'}`}>
                  {box.status === 'delivered' ? <CheckCircle className="w-6 h-6 text-brand-success" /> : box.status === 'shipped' ? <Truck className="w-6 h-6 text-brand-orange" /> : <Box className="w-6 h-6 text-brand-muted" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-brand-brown">{t('dash_box_for_month')} {MONTHS[box.box_month - 1]} {box.box_year}</p>
                  <p className="text-sm text-brand-muted">
                    {t(`dash_box_${box.status}`)}
                    {box.tracking_number && <span> • {t('dash_box_tracking')}: {box.tracking_number}</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SupportTab({ tickets, t }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/tickets`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ subject, message })
      });
      if (res.ok) {
        toast.success(t('dash_ticket_sent'));
        setSubject(''); setMessage('');
        window.location.reload();
      }
    } catch { toast.error('Error'); } finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-brand-border/30 p-6">
        <h3 className="font-heading font-semibold text-brand-brown mb-4 flex items-center gap-2">
          <Send className="w-4 h-4 text-brand-orange" /> {t('dash_new_ticket')}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1.5">{t('dash_ticket_subject')}</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} required className="input-field" data-testid="ticket-subject" />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1.5">{t('dash_ticket_message')}</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} required rows={4} className="input-field w-full resize-none" data-testid="ticket-message" />
          </div>
          <button type="submit" disabled={sending} className="btn-primary" data-testid="ticket-submit">
            {sending ? '...' : t('dash_ticket_send')}
          </button>
        </form>
      </div>

      {tickets.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-brand-brown">{t('dash_support_tab')}</h3>
          {tickets.map(ticket => (
            <div key={ticket.ticket_id} className="bg-white rounded-xl border border-brand-border/30 p-4" data-testid={`ticket-${ticket.ticket_id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-brand-brown">{ticket.subject}</p>
                <Badge className={ticket.status === 'open' ? 'bg-brand-orange' : 'bg-brand-success'}>{ticket.status}</Badge>
              </div>
              <p className="text-sm text-brand-muted">{ticket.message}</p>
              <p className="text-xs text-brand-muted mt-2">{new Date(ticket.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BadgeTab({ totalOrders, memberSince, t, language }) {
  const getBadgeLevel = () => {
    if (totalOrders >= 12) return 4;
    if (totalOrders >= 6) return 3;
    if (totalOrders >= 3) return 2;
    return 1;
  };

  const level = getBadgeLevel();
  const BadgeIcon = BADGE_ICONS[level];
  const badges = [
    { level: 1, name: language === 'ro' ? 'Pui de lup' : 'Puppy Pal', orders: 0, color: 'bg-brand-cream' },
    { level: 2, name: language === 'ro' ? 'Lăbuță de aur' : 'Golden Paw', orders: 3, color: 'bg-brand-orange/20' },
    { level: 3, name: language === 'ro' ? 'Prietenos VIP' : 'Top Dog', orders: 6, color: 'bg-brand-orange/40' },
    { level: 4, name: language === 'ro' ? 'Lider de haită' : 'Pack Leader', orders: 12, color: 'bg-brand-orange' },
  ];

  const currentBadge = badges[level - 1];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-brand-border/30 p-8 text-center">
        <div className={`w-24 h-24 ${currentBadge.color} rounded-full flex items-center justify-center mx-auto mb-4 ${level === 4 ? 'text-white' : 'text-brand-brown'}`}>
          <BadgeIcon className="w-12 h-12" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-brand-brown mb-1">{currentBadge.name}</h3>
        <p className="text-brand-muted mb-4">Level {level}</p>
        <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto text-sm">
          <div className="bg-brand-cream rounded-xl p-3">
            <p className="text-brand-muted">{t('dash_total_orders')}</p>
            <p className="font-heading text-xl font-bold text-brand-brown">{totalOrders}</p>
          </div>
          <div className="bg-brand-cream rounded-xl p-3">
            <p className="text-brand-muted">{t('dash_member_since')}</p>
            <p className="font-heading text-xl font-bold text-brand-brown">{memberSince}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {badges.map(badge => {
          const Icon = BADGE_ICONS[badge.level];
          const isActive = level >= badge.level;
          return (
            <div key={badge.level} className={`rounded-xl p-4 text-center ${isActive ? 'bg-white border border-brand-orange' : 'bg-brand-cream/50 opacity-50'}`}>
              <Icon className={`w-8 h-8 mx-auto mb-2 ${isActive ? 'text-brand-orange' : 'text-brand-muted'}`} />
              <p className="text-sm font-medium">{badge.name}</p>
              <p className="text-xs text-brand-muted">{badge.orders}+ boxes</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, loading: authLoading, checkAuth } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for auth_token in URL (from magic link login)
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get('auth_token');
    if (authToken) {
      try { localStorage.setItem('petsbox_session_token', authToken); } catch {}
      // Clean URL
      window.history.replaceState({}, '', '/dashboard');
      checkAuth();
      return;
    }

    if (!authLoading && !user) {
      navigate('/');
      return;
    }
    if (user) fetchData();
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      const [profileRes, subsRes, boxesRes, ticketsRes] = await Promise.all([
        fetch(`${API}/profile`, authOpts()),
        fetch(`${API}/subscriptions/my`, authOpts()),
        fetch(`${API}/subscriptions/my/boxes`, authOpts()),
        fetch(`${API}/tickets/my`, authOpts()),
      ]);
      if (profileRes.ok) setProfile(await profileRes.json());
      if (subsRes.ok) setSubscriptions(await subsRes.json());
      if (boxesRes.ok) setBoxes(await boxesRes.json());
      if (ticketsRes.ok) setTickets(await ticketsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: t('dash_profile_tab'), icon: User },
    { id: 'subscription', label: t('dash_subscription_tab'), icon: Package },
    { id: 'badge', label: t('dash_badge_tab'), icon: Award },
    { id: 'support', label: t('dash_support_tab'), icon: MessageSquare },
  ];

  const totalOrders = subscriptions.filter(s => s.status === 'active').length;
  const memberSince = user?.created_at ? new Date(user.created_at).getFullYear() : new Date().getFullYear();

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="dashboard-page">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="section-container">
          {/* Header */}
          <div className="bg-white rounded-3xl p-6 md:p-8 mb-8 border border-brand-border/30">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-20 h-20 bg-brand-orange rounded-2xl flex items-center justify-center shadow-lg">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full rounded-2xl object-cover" />
                ) : (
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 512 512"><path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5.3-86.2 32.6-96.8 70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7.9 78.6 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2-25.8 0-46.7-20.9-46.7-46.7v-1.6c0-10.4 1.6-20.8 5.2-30.5zM324.7 89.4c14.3-42.9 51.7-66.1 83.6-51.5 32 14.6 45.9 57.9 31.6 100.7-14.3 42.9-51.7 66.1-83.6 51.5-32-14.6-45.9-57.9-31.6-100.7zm89.1 164.5c24.5 14 29.1 51.7 10.2 84.1-18.9 32.4-54 46.1-78.5 32.1s-29.1-51.7-10.2-84.1c18.9-32.4 54-46.1 78.5-32.1z"/></svg>
                )}
              </div>
              <div className="text-center md:text-left">
                <h1 className="font-heading text-3xl font-bold text-brand-brown">
                  {t('dash_hello')}, {user?.name?.split(' ')[0]}!
                </h1>
                <p className="text-brand-muted">{t('dash_subtitle')}</p>
              </div>
              <div className="md:ml-auto flex items-center gap-4">
                <div className="text-center bg-brand-cream rounded-xl px-4 py-2">
                  <p className="text-xs text-brand-muted">{t('dash_active_subs')}</p>
                  <p className="font-heading text-2xl font-bold text-brand-orange">{subscriptions.filter(s => s.status === 'active').length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-brand-orange text-white'
                    : 'bg-white text-brand-muted hover:bg-brand-cream'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-brand-cream-dark/50 rounded-3xl p-6 md:p-8">
            {activeTab === 'profile' && <ProfileTab profile={profile} setProfile={setProfile} t={t} isRomanian={language === 'ro'} />}
            {activeTab === 'subscription' && <SubscriptionTab subscriptions={subscriptions} boxes={boxes} language={language} t={t} />}
            {activeTab === 'badge' && <BadgeTab totalOrders={totalOrders} memberSince={memberSince} t={t} language={language} />}
            {activeTab === 'support' && <SupportTab tickets={tickets} t={t} />}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
